/**
 * Watching for the thing the step asked the person to do.
 *
 * One hook, one gate, one boolean. The signal-specific work is in gateDetectors.js so that adding a
 * seventh signal is an entry in a table rather than a change in here or in the overlay.
 *
 * ── THE STALE-SATISFACTION BUG THIS IS SHAPED TO AVOID ───────────────────────────────────────────
 * A state update lands one render LATER than the prop that caused it. So for exactly one render
 * after the tour advances, `satisfied` still describes the PREVIOUS step — and since a satisfied
 * gate is what calls next(), that one render would advance the tour a second time and skip a step
 * nobody ever saw. The satisfaction therefore carries the step id it belongs to and is compared
 * DURING RENDER, which makes stale-true unrepresentable rather than merely unlikely. Same shape, and
 * the same reason, as the anchor status in useAnchorElement.js.
 */
import { useEffect, useState } from 'react'

import { watchGate, gateSignal } from './gateDetectors'

/**
 * @param {object|null} step        the active step (its `until` is what is watched)
 * @param {object} options
 * @param {boolean} [options.enabled]
 * @param {string} [options.pathname] the router's current path — the `route` signal's only input
 * @returns {boolean} has the person done it
 */
export function useStepGate(step, { enabled = true, pathname } = {}) {
  const [state, setState] = useState({ stepId: null, satisfied: false })

  const stepId = step ? step.id : null
  const until = step ? step.until : null
  const signal = gateSignal(until)
  // Only the `route` signal cares about the path, and only it should restart its watch when the path
  // moves. Restarting a `requested` subscription on every navigation would open a window in which
  // the very request the step is waiting for could pass unheard.
  const routeDep = signal === 'route' ? pathname : null

  useEffect(() => {
    if (!enabled || !stepId || !until) {
      setState({ stepId, satisfied: false })
      return undefined
    }
    setState({ stepId, satisfied: false })
    return watchGate(until, { pathname: routeDep }, () => {
      setState({ stepId, satisfied: true })
    })
    // `until` is frozen by defineTour and identity-stable per step, so stepId is the honest key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepId, enabled, signal, routeDep])

  return state.stepId === stepId && state.satisfied
}

export default useStepGate
